import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useVenue } from "@/components/shared/VenueContext";
import QuickLoader from "@/components/shared/QuickLoader";
import {
  LayoutDashboard, GraduationCap, BookOpen, Award, Route as RouteIcon,
  ShieldCheck, ClipboardList, AlertTriangle, FileText, FileCheck,
  Users, Building2, Settings, ArrowLeft, Sparkles, Menu, X, ChevronRight,
  ScrollText, BadgeCheck, Brain,
} from "lucide-react";

const NAV = [
  {
    section: "General",
    items: [
      { to: "/HosysAI",            label: "Dashboard",           icon: LayoutDashboard },
    ],
  },
  {
    section: "AI Compliance",
    items: [
      { to: "/HosysAI/Diagnostic", label: "Diagnòstic IA",       icon: ClipboardList },
      { to: "/HosysAI/Systems",    label: "Inventari IA",        icon: Brain },
      { to: "/HosysAI/Risk",       label: "Classificació risc",  icon: ShieldCheck, disabled: false },
      { to: "/HosysAI/Policies",   label: "Polítiques internes", icon: ScrollText, disabled: true },
      { to: "/HosysAI/Incidents",  label: "Incidències",         icon: AlertTriangle },
    ],
  },
  {
    section: "AI Academy",
    items: [
      { to: "/HosysAI/Courses",       label: "Formació",         icon: GraduationCap },
      { to: "/HosysAI/MyCourses",     label: "Els meus cursos",  icon: BookOpen },
      { to: "/HosysAI/Itineraries",   label: "Itineraris",       icon: RouteIcon, disabled: true },
      { to: "/HosysAI/Certificates",  label: "Certificats",      icon: Award, disabled: true },
    ],
  },
  {
    section: "Documentació",
    items: [
      { to: "/HosysAI/Dossier",   label: "Expedient IA",  icon: FileText, disabled: true },
      { to: "/HosysAI/Evidences", label: "Evidències",    icon: FileCheck, disabled: true },
    ],
  },
  {
    section: "Organització",
    items: [
      { to: "/HosysAI/Team",         label: "Treballadors",   icon: Users, disabled: true },
      { to: "/HosysAI/Organization", label: "Empresa",        icon: Building2, disabled: true },
      { to: "/HosysAI/Settings",     label: "Configuració",   icon: Settings, disabled: true },
    ],
  },
];

function NavLink({ to, label, icon: Icon, isActive, onClick, disabled }) {
  if (disabled) {
    return (
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-500 cursor-not-allowed">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-800/60 text-slate-400 border border-slate-700">Aviat</span>
      </div>
    );
  }
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
        isActive
          ? "bg-gradient-to-r from-cyan-400/20 to-fuchsia-500/20 text-white border border-cyan-400/30"
          : "text-slate-300 hover:bg-white/5 hover:text-white"
      }`}
    >
      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-cyan-300" : ""}`} />
      <span className="flex-1 truncate">{label}</span>
      {isActive && <ChevronRight className="w-3 h-3 text-cyan-300" />}
    </Link>
  );
}

export default function HosysAILayout({ children }) {
  const { user, loading, currentVenue } = useVenue();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) return <QuickLoader />;

  const isActive = (to) => {
    if (to === "/HosysAI") return location.pathname === "/HosysAI";
    return location.pathname.startsWith(to);
  };

  const Sidebar = ({ onClick }) => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-4 pt-5 pb-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/40">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-lg leading-none">HOSYS<span className="text-cyan-300"> AI</span></p>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Academy & Compliance</p>
          </div>
        </div>
        {currentVenue && (
          <div className="mt-3 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Empresa</p>
            <p className="text-xs text-white font-medium truncate">{currentVenue.name}</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        {NAV.map((section) => (
          <div key={section.section}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-3 mb-1.5">{section.section}</p>
            <div className="space-y-0.5">
              {section.items.map((it) => (
                <NavLink key={it.to} {...it} isActive={isActive(it.to)} onClick={onClick} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer — back to Hosys */}
      <div className="px-3 pb-4 pt-3 border-t border-white/10">
        {(() => {
          const isStandalone = (process.env.REACT_APP_MODE || "").toLowerCase() === "hosysai"
            || (typeof window !== "undefined" && (window.location.hostname === "ai.ho-sys.com" || window.location.hostname.startsWith("ai.")));
          if (isStandalone) {
            return (
              <a
                href="https://www.ho-sys.com"
                onClick={onClick}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
                data-testid="hosysai-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Anar a Ho-Sys
              </a>
            );
          }
          return (
            <Link
              to="/Dashboard"
              onClick={onClick}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="hosysai-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Tornar a Ho-Sys
            </Link>
          );
        })()}
        <p className="text-[9px] text-slate-500 mt-3 px-3">v0.1 · Sub-fase 1A</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-slate-900/80 backdrop-blur-xl border-r border-white/10 z-30" data-testid="hosysai-sidebar">
        <Sidebar />
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 bg-slate-950/90 backdrop-blur-lg border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <p className="text-white font-bold">HOSYS<span className="text-cyan-300"> AI</span></p>
        </div>
        <button onClick={() => setMobileOpen(true)} className="p-2 rounded-lg hover:bg-white/5" data-testid="hosysai-mobile-open">
          <Menu className="w-5 h-5 text-white" />
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-slate-900 border-r border-white/10">
            <div className="flex justify-end p-3">
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-white/5" data-testid="hosysai-mobile-close">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <Sidebar onClick={() => setMobileOpen(false)} />
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <main className="lg:pl-64 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-8 text-slate-100">
          {children}
        </div>
      </main>
    </div>
  );
}
